/**
 * RecoverCann Super Shopper Module
 * Allows staff to search for customers and impersonate them for creating draft orders
 * Based on RecoverCann Swell implementation
 */

(function() {
  'use strict';

  // ===== SAFE STORAGE HELPERS =====
  // sessionStorage can throw in private browsing mode on some browsers
  function safeSessionGet(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      console.warn('[SuperShopper] sessionStorage unavailable:', e);
      return null;
    }
  }
  
  function safeSessionSet(key, value) {
    try {
      sessionStorage.setItem(key, value);
      return true;
    } catch (e) {
      console.warn('[SuperShopper] sessionStorage unavailable:', e);
      return false;
    }
  }
  
  function safeSessionRemove(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (e) {
      console.warn('[SuperShopper] sessionStorage unavailable:', e);
    }
  }

  // ===== HTML ESCAPE FOR XSS PREVENTION =====
  function escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  const container = document.getElementById('super-shopper-controls');
  const resolvedApiBase = container?.dataset?.apiBase
    || document.querySelector('[data-api-base]')?.getAttribute('data-api-base')
    || 'https://recovercann-shopify.vercel.app';
  const API_BASE = String(resolvedApiBase).replace(/\/+$/, '');
  let searchTimeout = null;
  let selectedCustomerId = null;

  class SuperShopperManager {
    constructor() {
      this.toggle = document.getElementById('super-shopper-enable');
      this.searchSection = document.getElementById('super-shopper-search');
      this.activeSection = document.getElementById('super-shopper-active');
      this.searchInput = document.getElementById('customer-search-input');
      this.searchResults = document.getElementById('search-results');
      this.impersonatedName = document.getElementById('impersonated-customer-name');
      this._hideTimers = new WeakMap();

      if (!this.toggle) return;

      this.staffCustomerId = this.toggle.dataset.customerId;
      this.init();
    }

    setSectionVisibility(section, isVisible, options = {}) {
      if (!section) return;

      const { immediate = false } = options;
      const pendingHideTimer = this._hideTimers.get(section);
      if (pendingHideTimer) {
        clearTimeout(pendingHideTimer);
        this._hideTimers.delete(section);
      }

      if (isVisible) {
        section.hidden = false;
        if (immediate) {
          section.classList.add('is-visible');
        } else {
          requestAnimationFrame(() => {
            section.classList.add('is-visible');
          });
        }
        return;
      }

      section.classList.remove('is-visible');
      if (immediate) {
        section.hidden = true;
        return;
      }

      const hideTimer = setTimeout(() => {
        section.hidden = true;
        this._hideTimers.delete(section);
      }, 220);
      this._hideTimers.set(section, hideTimer);
    }

    setMode(mode, options = {}) {
      const showSearch = mode === 'search';
      const showActive = mode === 'active';

      this.setSectionVisibility(this.searchSection, showSearch, options);
      this.setSectionVisibility(this.activeSection, showActive, options);
    }

    // Format date string for display (e.g., "Jan 15, 1990")
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          year: 'numeric' 
        });
      } catch (e) {
        return dateString;
      }
    }

    init() {
      // Check if already in super shopper mode (from metafields)
      this.checkExistingState();

      // Toggle event
      this.toggle.addEventListener('change', (e) => {
        if (e.target.checked) {
          this.enableSuperShopper();
        } else {
          this.disableSuperShopper();
        }
      });

      // Search input event
      if (this.searchInput) {
        this.searchInput.addEventListener('input', (e) => {
          clearTimeout(searchTimeout);
          const query = e.target.value.trim();

          if (query.length < 2) {
            if (this.searchResults) this.searchResults.innerHTML = '';
            return;
          }

          searchTimeout = setTimeout(() => {
            this.searchCustomers(query);
          }, 300);
        });
      }

      // Event delegation for search results (prevents memory leaks)
      if (this.searchResults) {
        this.searchResults.addEventListener('click', (e) => {
          const item = e.target.closest('.search-result-item');
          if (item) {
            const customerId = item.dataset.customerId;
            const customerName = item.querySelector('strong').textContent;
            const customerEmail = item.dataset.customerEmail || '';
            const customerPhone = item.dataset.customerPhone || '';
            this.selectCustomer(customerId, customerName, customerEmail, customerPhone);
          }
        });
      }
    }

    async checkExistingState() {
      // Check sessionStorage for existing super shopper state
      const storedCustomerId = safeSessionGet('superShopperCustomerId');
      const storedCustomerName = safeSessionGet('superShopperCustomerName');
      const storedCustomerEmail = safeSessionGet('superShopperCustomerEmail');
      const storedCustomerPhone = safeSessionGet('superShopperCustomerPhone');

      // Validate the stored ID is a valid numeric customer ID
      const isValidId = storedCustomerId && /^\d+$/.test(storedCustomerId) && storedCustomerId !== 'none';

      if (isValidId && storedCustomerName) {
        // Restore the UI state
        selectedCustomerId = storedCustomerId;
        this.toggle.checked = true;
        this.setMode('active', { immediate: true });
        if (this.impersonatedName) this.impersonatedName.textContent = storedCustomerName;

        // Dispatch event so other modules know
        document.dispatchEvent(new CustomEvent('superShopper:customerSelected', {
          detail: {
            customerId: storedCustomerId,
            customerName: storedCustomerName,
            customerEmail: storedCustomerEmail || '',
            customerPhone: storedCustomerPhone || ''
          }
        }));
      } else if (storedCustomerId) {
        // Invalid stored data - clear it
        safeSessionRemove('superShopperCustomerId');
        safeSessionRemove('superShopperCustomerName');
        safeSessionRemove('superShopperCustomerEmail');
        safeSessionRemove('superShopperCustomerPhone');
      }
    }

    enableSuperShopper() {
      this.setMode('search');
      if (this.searchInput) this.searchInput.focus();
    }

    async disableSuperShopper() {
      try {
        const response = await fetch(`${API_BASE}/api/staff/enable-super-shopper`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerId: this.staffCustomerId,
            enabled: false
          })
        });

        // Even if API fails, clear local state so user can retry
        // Clear sessionStorage
        safeSessionRemove('superShopperCustomerId');
        safeSessionRemove('superShopperCustomerName');
        safeSessionRemove('superShopperCustomerEmail');
        safeSessionRemove('superShopperCustomerPhone');

        this.setMode('idle');
        if (this.searchInput) this.searchInput.value = '';
        if (this.searchResults) this.searchResults.innerHTML = '';
        selectedCustomerId = null;

        // Dispatch event to notify other modules
        document.dispatchEvent(new CustomEvent('superShopper:disabled'));

        if (!response.ok) {
          console.error('API returned error when disabling, but local state cleared');
        }

      } catch (error) {
        console.error('Error disabling super shopper:', error);
        // Still clear local state even on error
        safeSessionRemove('superShopperCustomerId');
        safeSessionRemove('superShopperCustomerName');
        safeSessionRemove('superShopperCustomerEmail');
        safeSessionRemove('superShopperCustomerPhone');
        this.setMode('idle');
        selectedCustomerId = null;
        document.dispatchEvent(new CustomEvent('superShopper:disabled'));
      }
    }

    async searchCustomers(query) {
      if (this.searchResults) this.searchResults.innerHTML = '<div class="loading">Searching...</div>';

      try {
        const response = await fetch(`${API_BASE}/api/staff/search-customers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query, staffCustomerId: this.staffCustomerId })
        });

        if (!response.ok) {
          throw new Error('Search failed');
        }

        const data = await response.json();
        this.renderSearchResults(data.customers || []);
      } catch (error) {
        console.error('Error searching customers:', error);
        if (this.searchResults) this.searchResults.innerHTML = '<div class="error">Search failed</div>';
      }
    }

    renderSearchResults(customers) {
      if (!this.searchResults) return;
      
      if (customers.length === 0) {
        this.searchResults.innerHTML = '<div class="no-results">No customers found</div>';
        return;
      }

      const html = customers.map(customer => {
        const approved = customer.prescriptionApproved ? '✓' : '✗';
        const limit = customer.monthlyLimit ? `${customer.monthlyLimit}g` : 'N/A';
        const dob = customer.dateOfBirth ? this.formatDate(customer.dateOfBirth) : 'N/A';
        // Escape user data to prevent XSS
        const safeName = escapeHtml(`${customer.firstName} ${customer.lastName}`);
        const safeEmail = escapeHtml(customer.email);
        const safePhone = escapeHtml(customer.phone || '');
        const safeId = escapeHtml(String(customer.id));

        return `
          <div class="search-result-item" data-customer-id="${safeId}" data-customer-email="${safeEmail}" data-customer-phone="${safePhone}">
            <div class="customer-info">
              <strong>${safeName}</strong>
              <span class="customer-email">${safeEmail}</span>
              <span class="customer-dob">DOB: ${dob}</span>
            </div>
            <div class="customer-meta">
              <span class="prescription-status ${customer.prescriptionApproved ? 'approved' : 'not-approved'}">
                Rx: ${approved}
              </span>
              <span class="limit-info">Limit: ${limit}</span>
            </div>
          </div>
        `;
      }).join('');

      this.searchResults.innerHTML = html;
    }

    async selectCustomer(customerId, customerName, customerEmail, customerPhone) {
      selectedCustomerId = customerId;

      // Show loading state
      if (this.searchResults) this.searchResults.innerHTML = '<div class="loading">Selecting customer...</div>';

      try {
        const response = await fetch(`${API_BASE}/api/staff/enable-super-shopper`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerId: this.staffCustomerId,
            targetCustomerId: customerId,
            enabled: true
          })
        });

        if (!response.ok) {
          throw new Error('Failed to enable super shopper');
        }

        // Store in sessionStorage for persistence across page loads
        safeSessionSet('superShopperCustomerId', customerId);
        safeSessionSet('superShopperCustomerName', customerName);
        safeSessionSet('superShopperCustomerEmail', customerEmail || '');
        safeSessionSet('superShopperCustomerPhone', customerPhone || '');

        // Update UI without reloading
        this.setMode('active');
        if (this.impersonatedName) this.impersonatedName.textContent = customerName;
        this.toggle.checked = true;
        if (this.searchInput) this.searchInput.value = '';

        // Dispatch event for other modules (like Quick Shop) to react
        document.dispatchEvent(new CustomEvent('superShopper:customerSelected', {
          detail: {
            customerId,
            customerName,
            customerEmail: customerEmail || '',
            customerPhone: customerPhone || ''
          }
        }));

      } catch (error) {
        console.error('Error selecting customer:', error);
        if (this.searchResults) this.searchResults.innerHTML = '<div class="error">Failed to select customer. Please try again.</div>';
      }
    }

    // Get current selected customer info (for other modules)
    getSelectedCustomer() {
      if (this.activeSection && !this.activeSection.hidden) {
        return {
          name: this.impersonatedName?.textContent || null,
          isActive: true
        };
      }
      return { name: null, isActive: false };
    }
  }

  // Expose globally for other modules
  window.RecoverCannSuperShopper = null;

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.RecoverCannSuperShopper = new SuperShopperManager();
    });
  } else {
    window.RecoverCannSuperShopper = new SuperShopperManager();
  }
})();
